import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import SimpleMantenedorLayout from '../SimpleMantenedorLayout'
import { useMantenedores } from '../../context/MantenedoresContext'
import { useAsignacionesExtras } from '../../hooks/useAsignacionesExtras'
import AsignacionModal from './AsignacionModal'
import AsignacionesHeader from './AsignacionesHeader'
import AsignacionesTable from './AsignacionesTable'
import EditarAsignacionModal from './EditarAsignacionModal'
import ConfirmarAccionModal from './ConfirmarAccionModal'
import AsignacionesFiltros from './AsignacionesFiltros'
import AsignacionesFiltrosButton from './AsignacionesFiltrosButton'
import BulkEditAsignacionesModal from './BulkEditAsignacionesModal'
import AsignacionesPaginacion from './AsignacionesPaginacion'
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal'
import Swal from 'sweetalert2'
import BulkUploadAsignacionesModal from './BulkUploadAsignacionesModal'
import {
    downloadPlantillaAsignacionesBulk,
    uploadAsignacionesBulk
} from '@shared/helpers/api/mantenedores/extras'

const AsignacionExtras = () => {
    const user = useSelector(state => state.auth.user)
    const { userPermissions } = useMantenedores()
    const permisos = userPermissions?.modules?.extras || {}

    // Hook para asignaciones
    const {
        asignaciones,
        loading,
        error,
        total,
        pageNumber,
        pageSize,
        totalPages,
        filtros,
        refreshAsignaciones,
        formatearMonto,
        formatearFecha,
        getBadgeVariant,
        getStatusBadgeVariant,
        permisos: hookPermisos,
        actualizarAsignacion,
        bulkUpdateAsignacionesStatus,
        handleFiltersChange,
        handlePageChange
    } = useAsignacionesExtras(user, permisos)

    // Usar permisos del hook si están disponibles
    const permisosActivos = hookPermisos?.canView !== undefined ? hookPermisos : permisos

    // Verificar si puede crear asignaciones (administrar o solicitar)
    const canCreateAsignacion = permisosActivos.canAdmin || permisosActivos.canRequest

    // Modal de nueva asignación
    const [showModal, setShowModal] = useState(false)
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
    const [downloadingPlantilla, setDownloadingPlantilla] = useState(false)
    
    // Modal de filtros
    const [showFiltrosModal, setShowFiltrosModal] = useState(false)
    const handleNuevaAsignacion = () => {
        if (!canCreateAsignacion) return
        setShowModal(true)
    }
    const handleCloseModal = () => setShowModal(false)
    const handleAsignacionCreada = () => {
        setShowModal(false)
        refreshAsignaciones()
    }

    const handleOpenBulkUpload = () => {
        if (!canCreateAsignacion) return
        setShowBulkUploadModal(true)
    }

    const handleCloseBulkUpload = () => {
        setShowBulkUploadModal(false)
    }

    const handleDownloadPlantillaBulk = async () => {
        setDownloadingPlantilla(true)
        try {
            const result = await downloadPlantillaAsignacionesBulk(user)
            if (result.success) {
                SuccessSwal('Plantilla Descargada', result.message || 'La plantilla se descargó correctamente', false)
            } else {
                ErrorSwal('Error al Descargar Plantilla', result.error || 'No fue posible descargar la plantilla', false)
            }
        } catch (error) {
            ErrorSwal('Error al Descargar Plantilla', 'Ocurrió un error inesperado al descargar la plantilla', false)
        } finally {
            setDownloadingPlantilla(false)
        }
    }

    const handleUploadBulkAsignaciones = async file => {
        try {
            const result = await uploadAsignacionesBulk(user, file)

            if (result.success) {
                handleCloseBulkUpload()

                await Swal.fire({
                    icon: 'success',
                    title: 'Proceso de Carga Iniciado',
                    html: `
                        <div style="text-align: left;">
                            <p><strong>El archivo se está procesando de manera asíncrona.</strong></p>
                            <br/>
                            <p><strong>Información del proceso:</strong></p>
                            <ul style="list-style-position: inside;">
                                <li><strong>Filas a procesar:</strong> ${result.totalFilas || 0}</li>
                                <li><strong>Process ID:</strong> ${result.processId || 'N/A'}</li>
                            </ul>
                            <br/>
                            <p style="color: #856404; background-color: #fff3cd; padding: 10px; border-radius: 5px; border-left: 4px solid #ffc107;">
                                <strong>Importante:</strong> Si falla una fila, el backend hará rollback total del archivo.
                            </p>
                            <br/>
                            <p style="color: #004085; background-color: #cce5ff; padding: 10px; border-radius: 5px; border-left: 4px solid #007bff;">
                                <strong>Notificación:</strong> Recibirás un correo automático cuando el proceso finalice (éxito o error).
                            </p>
                        </div>
                    `,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#28a745',
                    width: '620px'
                })

                refreshAsignaciones()
            } else {
                let errorHtml = `<div style="text-align: left;">`

                if (result.error) {
                    errorHtml += `<p><strong>${result.error}</strong></p>`
                }

                if (result.total_errores) {
                    errorHtml += `<br/><p style="color: #721c24; background-color: #f8d7da; padding: 10px; border-radius: 5px; border-left: 4px solid #dc3545;">`
                    errorHtml += `<strong>Total de errores encontrados:</strong> ${result.total_errores}`
                    if (result.fase) {
                        errorHtml += `<br/><strong>Fase:</strong> ${result.fase}`
                    }
                    errorHtml += `</p>`
                }

                if (result.errores && result.errores.length > 0) {
                    errorHtml += `<br/><p><strong>Primeros errores detectados:</strong></p><ul style="list-style-position: inside; margin-left: 10px;">`

                    const errorsToShow = result.errores.slice(0, 5)
                    errorsToShow.forEach(err => {
                        errorHtml += `<li style="margin-bottom: 8px;">`
                        errorHtml += `<strong>Fila ${err.fila || '-'}</strong> - `
                        if (err.campo) {
                            errorHtml += `<em>${err.campo}:</em> `
                        }
                        if (err.valor) {
                            errorHtml += `<strong>${err.valor}</strong> - `
                        }
                        errorHtml += `${err.mensaje || err.error || 'Error de validación'}`
                        errorHtml += `</li>`
                    })

                    errorHtml += `</ul>`

                    if (result.errores.length > 5) {
                        errorHtml += `<p style="color: #856404; font-style: italic;">... y ${result.errores.length - 5} error(es) adicional(es)</p>`
                    }
                }

                errorHtml += `</div>`

                await Swal.fire({
                    icon: 'error',
                    title: 'Error al Procesar Archivo',
                    html: errorHtml,
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#dc3545',
                    width: '700px'
                })
            }
        } catch (error) {
            ErrorSwal('Error al Cargar Asignaciones', 'Ocurrió un error inesperado al procesar el archivo', false)
        }
    }

    // Modal de confirmación de acción (aprobar, rechazar, cancelar)
    const [showConfirm, setShowConfirm] = useState(false)
    const [accionSeleccionada, setAccionSeleccionada] = useState(null) // {asignacion, accion}
    const [accionLoading, setAccionLoading] = useState(false)
    const [accionError, setAccionError] = useState(null)

    const handleAccion = (asignacion, accion) => {
        setAccionSeleccionada({ asignacion, accion })
        setShowConfirm(true)
        setAccionError(null)
    }
    const handleConfirmAccion = async () => {
        if (!accionSeleccionada) return
        setAccionLoading(true)
        setAccionError(null)
        let estado = ''
        if (accionSeleccionada.accion === 'aprobar') estado = 'aprobado'
        if (accionSeleccionada.accion === 'rechazar') estado = 'rechazado'
        if (accionSeleccionada.accion === 'cancelar') estado = 'cancelado'
        try {
            const res = await actualizarAsignacion(accionSeleccionada.asignacion.id, { estado })
            if (res.success) {
                setShowConfirm(false)
                setAccionSeleccionada(null)
                refreshAsignaciones()
            } else {
                setAccionError(res.error || 'Error al actualizar asignación')
            }
        } catch (e) {
            setAccionError('Error al actualizar asignación')
        } finally {
            setAccionLoading(false)
        }
    }
    const handleCloseConfirm = () => {
        setShowConfirm(false)
        setAccionSeleccionada(null)
        setAccionError(null)
    }

    // Modal de edición
    const [showEdit, setShowEdit] = useState(false)
    const [editAsignacion, setEditAsignacion] = useState(null)
    const [editLoading, setEditLoading] = useState(false)
    const [editError, setEditError] = useState(null)

    // Estados para selección múltiple y edición masiva
    const [selectedAsignaciones, setSelectedAsignaciones] = useState([])
    const [showBulkEdit, setShowBulkEdit] = useState(false)
    const [bulkEditLoading, setBulkEditLoading] = useState(false)

    const handleEdit = asignacion => {
        setEditAsignacion(asignacion)
        setShowEdit(true)
        setEditError(null)
    }

    const handleEditSave = async (formData) => {
        if (!editAsignacion) return
        setEditLoading(true)
        setEditError(null)
        try {
            const res = await actualizarAsignacion(editAsignacion.id, {
                fecha_asignacion: formData.fecha_asignacion, // Cambio de mes_asignacion a fecha_asignacion
                cantidad: formData.cantidad,
                monto: formData.monto
            })
            if (res.success) {
                setShowEdit(false)
                setEditAsignacion(null)
                refreshAsignaciones()
            } else {
                setEditError(res.error || 'Error al editar asignación')
            }
        } catch (e) {
            setEditError('Error al editar asignación')
        } finally {
            setEditLoading(false)
        }
    }

    const handleCloseEdit = () => {
        setShowEdit(false)
        setEditAsignacion(null)
        setEditError(null)
    }

    // Funciones para selección múltiple
    const handleAsignacionSelect = (asignacionId, selected) => {
        setSelectedAsignaciones(prev =>
            selected
                ? [...prev, asignacionId]
                : prev.filter(id => id !== asignacionId)
        )
    }

    const handleSelectAllAsignaciones = (selected) => {
        if (selected) {
            setSelectedAsignaciones(asignaciones.map(asignacion => asignacion.id))
        } else {
            setSelectedAsignaciones([])
        }
    }

    // Funciones para edición masiva
    const handleOpenBulkEdit = () => {
        setShowBulkEdit(true)
    }

    const handleCloseBulkEdit = () => {
        setShowBulkEdit(false)
    }

    const handleBulkEditConfirm = async (action, asignacionIds) => {
        setBulkEditLoading(true)
        try {
            // Mapear acciones a estados correctos
            const estadoMapping = {
                'aprobar': 'aprobado',
                'rechazar': 'rechazado',
                'cancelar': 'cancelado'
            }

            const result = await bulkUpdateAsignacionesStatus({
                asignacion_ids: asignacionIds,
                estado: estadoMapping[action] || action
            })

            if (result.success) {
                setShowBulkEdit(false)
                setSelectedAsignaciones([])
                refreshAsignaciones()
                
                // Mensaje de éxito con Swal
                const actionText = action === 'aprobar' ? 'aprobadas' : 
                                 action === 'rechazar' ? 'rechazadas' : 'canceladas'
                SuccessSwal(
                    'Operación Exitosa',
                    `Se ${actionText} ${asignacionIds.length} asignaciones exitosamente`,
                    false
                )
            } else {
                ErrorSwal(
                    'Error en la Operación',
                    'Error al actualizar asignaciones: ' + result.error,
                    false
                )
            }
        } catch (error) {
            console.error('Error en edición masiva:', error)
            ErrorSwal(
                'Error en la Operación',
                'Error al procesar la edición masiva',
                false
            )
        } finally {
            setBulkEditLoading(false)
        }
    }

    // Funciones para modal de filtros
    const handleOpenFiltros = () => {
        setShowFiltrosModal(true)
    }

    const handleCloseFiltros = () => {
        setShowFiltrosModal(false)
    }

    // Contar filtros activos
    const getActiveFiltersCount = () => {
        let count = 0
        if (filtros.searchValue?.trim()) count++
        if (filtros.usuario_movil_id) count++
        if (filtros.tipo_extra_id) count++
        if (filtros.extra_id) count++
        if (filtros.empresa_id) count++
        if (filtros.fecha_inicio) count++
        if (filtros.fecha_fin) count++
        if (filtros.estado_id) count++
        return count
    }

    return (
        <SimpleMantenedorLayout
            mantenedorId="extras"
            title="Asignación de Extras"
            subtitle="Gestión de asignaciones de extras a conductores"
            iconColor="#00b894"
            breadcrumbs={[{ label: 'Extras', path: '/mantenedores/extras' }, { label: 'Asignaciones' }]}
            showBackButton={false}
        >
            {/* Header con botón Nueva Asignación */}
            <AsignacionesHeader
                canCreateAsignacion={canCreateAsignacion}
                onNuevaAsignacion={handleNuevaAsignacion}
                onCargaMasiva={handleOpenBulkUpload}
            />

            {/* Botón de Filtros */}
            <AsignacionesFiltrosButton
                onOpenFilters={handleOpenFiltros}
                activeFiltersCount={getActiveFiltersCount()}
                onBulkEdit={handleOpenBulkEdit}
                selectedCount={selectedAsignaciones.length}
            />

            {/* Tabla de Asignaciones */}
            <AsignacionesTable
                asignaciones={asignaciones}
                loading={loading}
                error={error}
                total={total}
                permisosActivos={permisosActivos}
                formatearMonto={formatearMonto}
                formatearFecha={formatearFecha}
                getBadgeVariant={getBadgeVariant}
                getStatusBadgeVariant={getStatusBadgeVariant}
                onAccion={handleAccion}
                onEdit={handleEdit}
                canCreateAsignacion={canCreateAsignacion}
                selectedAsignaciones={selectedAsignaciones}
                onAsignacionSelect={handleAsignacionSelect}
                onSelectAll={handleSelectAllAsignaciones}
            />

            {/* Paginación */}
            <AsignacionesPaginacion
                pageNumber={pageNumber}
                totalPages={totalPages}
                total={total}
                pageSize={pageSize}
                loading={loading}
                onPageChange={handlePageChange}
            />

            {/* Modal de Nueva Asignación */}
            <AsignacionModal
                show={showModal}
                onHide={handleCloseModal}
                onAsignacionCreada={handleAsignacionCreada}
                user={user}
            />

            {/* Modal de Confirmación de Acción */}
            <ConfirmarAccionModal
                show={showConfirm}
                onHide={handleCloseConfirm}
                accionSeleccionada={accionSeleccionada}
                onConfirm={handleConfirmAccion}
                loading={accionLoading}
                error={accionError}
            />

            {/* Modal de Edición */}
            <EditarAsignacionModal
                show={showEdit}
                onHide={handleCloseEdit}
                asignacion={editAsignacion}
                onSave={handleEditSave}
                loading={editLoading}
                error={editError}
            />

            {/* Modal de Filtros */}
            <AsignacionesFiltros
                show={showFiltrosModal}
                onHide={handleCloseFiltros}
                user={user}
                onFiltersChange={handleFiltersChange}
                initialFilters={filtros}
            />

            {/* Modal de Edición Masiva */}
            <BulkEditAsignacionesModal
                show={showBulkEdit}
                onHide={handleCloseBulkEdit}
                selectedAsignaciones={selectedAsignaciones}
                asignaciones={asignaciones}
                onConfirm={handleBulkEditConfirm}
                loading={bulkEditLoading}
                formatearMonto={formatearMonto}
            />

            <BulkUploadAsignacionesModal
                show={showBulkUploadModal}
                onHide={handleCloseBulkUpload}
                onDownloadPlantilla={handleDownloadPlantillaBulk}
                onUploadFile={handleUploadBulkAsignaciones}
                loading={loading}
                downloadingPlantilla={downloadingPlantilla}
            />
        </SimpleMantenedorLayout>
    )
}

export default AsignacionExtras
